import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles, getArticlesByCategory } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

const categorySchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      name: '17c.com 分类浏览',
      url: 'https://17c.com/category',
      description: '浏览17c.com所有内容分类，包括最新入口、使用指南、安全须知、常见问题等',
      isPartOf: {
        '@type': 'WebSite',
        name: '17c.com',
        url: 'https://17c.com',
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://17c.com/' },
        { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://17c.com/category' },
      ],
    },
  ],
};

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'all';

  const handleTabChange = (slug: string) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (slug === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ tab: slug });
    }
  };

  const filteredArticles: Article[] = useMemo(() => {
    if (activeTab === 'all') {
      return [...articles].sort(
        (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
      );
    }
    const category = categories.find((c) => c.slug === activeTab);
    if (!category) return [];
    return getArticlesByCategory(category.id).sort(
      (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
    );
  }, [activeTab]);

  const activeCategoryName =
    activeTab === 'all' ? '全部文章' : categories.find((c) => c.slug === activeTab)?.name || '';

  return (
    <>
      <SEO
        title={`${activeCategoryName} - 17c.com 分类浏览`}
        description={`浏览17c.com的${activeCategoryName}内容，获取最新官方地址、使用指南、安全须知等实用信息`}
        canonicalUrl="https://17c.com/category"
      />
      <SchemaOrg data={categorySchema} id="category" />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '分类浏览' }]} />
              <div className="pb-6">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  分类浏览
                </h1>
                <p className="text-foreground-600 text-sm">
                  按分类浏览所有内容，快速找到您需要的信息
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="border-b border-background-200/70 -mt-px">
              <div className="flex items-center gap-1 overflow-x-auto py-3 -mx-1 px-1">
                <button
                  onClick={() => handleTabChange('all')}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                    activeTab === 'all'
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  全部文章
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleTabChange(cat.slug)}
                    className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                      activeTab === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="py-10">
              {filteredArticles.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                    <i className="ri-inbox-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500">暂无相关文章</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="bg-background-100/70 rounded-lg overflow-hidden hover:bg-background-100 transition-colors group"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full">
                            {article.categoryName}
                          </span>
                          <span className="text-xs text-foreground-400">{article.readTime}</span>
                        </div>
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-500 line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <span>{article.publishDate}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {article.views.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}